import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX, Zap, ZapOff } from "lucide-react";
import "@/App.css";
import Starfield from "@/components/Starfield";
import Intro from "@/components/Intro";
import Hero from "@/components/Hero";
import Portrait from "@/components/Portrait";
import Marquee from "@/components/Marquee";
import Awards from "@/components/Awards";
import Oracle from "@/components/Oracle";
import Letter from "@/components/Letter";
import WishWall from "@/components/WishWall";
import Finale from "@/components/Finale";
import SharePoster from "@/components/SharePoster";
import { sound } from "@/lib/audio";

function Logo() {
    return (
        <div className="flex items-center gap-2.5 rounded-full border border-[#F3D085]/25 bg-[#0B0716]/60 py-1.5 pl-1.5 pr-3 backdrop-blur" data-testid="site-logo">
            <svg width="30" height="30" viewBox="0 0 64 64" aria-hidden="true">
                <circle cx="32" cy="32" r="24" fill="none" stroke="#F3D085" strokeWidth="1.6" opacity="0.9" />
                <path d="M32 14l2.6 7.4L42 24l-7.4 2.6L32 34l-2.6-7.4L22 24l7.4-2.6z" fill="#F3D085" />
                <text x="32" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontStyle="italic" fontSize="18" fill="#F3D085">M</text>
            </svg>
            <span className="font-mono2 hidden text-[9px] uppercase tracking-[0.3em] text-[#F3D085]/70 sm:block">
                The M.M. Experience
            </span>
        </div>
    );
}

function App() {
    const [introDone, setIntroDone] = useState(false);
    const [soundOn, setSoundOn] = useState(false);
    const [reduce, setReduce] = useState(false);
    const finaleRef = useRef(null);
    const lenisRef = useRef(null);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReduce(mq.matches);
        const onChange = (e) => setReduce(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    useEffect(() => {
        document.documentElement.classList.toggle("reduce-motion", reduce);
    }, [reduce]);

    useEffect(() => {
        if (reduce) return undefined;
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
        raf = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
    }, [reduce]);

    const replay = () => {
        finaleRef.current?.relight();
        const el = document.getElementById("finale");
        if (!el) return;
        if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -20 });
        else el.scrollIntoView({ behavior: reduce ? "auto" : "smooth" });
    };

    return (
        <div className="grain relative min-h-screen overflow-x-clip bg-[#0B0716] text-[#FBF7EE]">
            <Starfield reduce={reduce} />

            <AnimatePresence>
                {!introDone && <Intro key="intro" reduce={reduce} onDone={() => setIntroDone(true)} />}
            </AnimatePresence>

            <motion.header
                className="fixed inset-x-0 top-0 z-[90] flex items-center justify-between px-5 py-4"
                initial={{ opacity: 0, y: -16 }}
                animate={introDone ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.3 }}
                style={{ pointerEvents: introDone ? "auto" : "none" }}
            >
                <Logo />
                <div className="flex items-center gap-2.5">
                    <button
                        data-testid="reduced-motion-toggle-button"
                        onClick={() => setReduce((r) => !r)}
                        aria-pressed={reduce}
                        aria-label={reduce ? "Enable full motion" : "Reduce motion"}
                        title={reduce ? "Enable full motion" : "Reduce motion"}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F3D085]/30 bg-[#0B0716]/60 text-[#F3D085]/80 backdrop-blur transition-colors hover:border-[#F3D085]/70 hover:text-[#F3D085]"
                    >
                        {reduce ? <ZapOff className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
                    </button>
                    <button
                        data-testid="music-toggle-button"
                        onClick={() => setSoundOn(sound.toggle())}
                        aria-pressed={soundOn}
                        aria-label={soundOn ? "Turn celebration music off" : "Turn celebration music on"}
                        title={soundOn ? "Music off" : "Music on (off by default)"}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F3D085]/30 bg-[#0B0716]/60 text-[#F3D085]/80 backdrop-blur transition-colors hover:border-[#F3D085]/70 hover:text-[#F3D085]"
                    >
                        {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                    </button>
                </div>
            </motion.header>

            <main aria-hidden={!introDone}>
                <Hero active={introDone} reduce={reduce} />
                <Portrait reduce={reduce} />
                <Marquee
                    testId="marquee-awards"
                    phrases={["Certified Fabulous", "Universally Appreciated", "CEO of Good Vibes", "Lifetime Cake Privileges", "Officially Legendary"]}
                />
                <Awards reduce={reduce} />
                <Oracle reduce={reduce} />
                <Letter reduce={reduce} />
                <WishWall />
                <Marquee
                    testId="marquee-finale"
                    phrases={["More Joy", "More Abundance", "More Unforgettable Moments", "Happy Birthday Mary"]}
                />
                <Finale ref={finaleRef} reduce={reduce} />
                <SharePoster onReplay={replay} reduce={reduce} />
            </main>
        </div>
    );
}

export default App;
